/**
 * 에피소드 목록 필터의 축 정의와 적용 조건 칩(순수 계산).
 *
 * 화면(`EpisodeFilterLane`)은 여기서 받은 축을 그리기만 한다. 칩 문구와 해제 결과도 이 모듈이
 * 만든다. 한 곳에 두는 이유는 **드롭다운 문구와 칩 문구를 같게** 하기 위해서다.
 * 둘을 다른 곳에서 만들면 문구가 달라진다. 예: `결함: 종료 방식` 을 골랐는데 칩에는
 * `quality_category: termination` 이 표시된다.
 *
 * 축은 네 가지 형태다:
 *   - 단일 선택 판정 축(검토·표시·검증·결함 수용·결과)
 *   - 다중 선택 태그 축(결함·행동 출처). 값, 갈래, `있음`/`없음` 을 한 드롭다운에 둔다.
 *   - 단일 선택 계산값 축(영상 정지 후보). 모양은 판정 축과 같고 "계산값" 구분선 아래에 둔다.
 *   - 밴드 두 축과 벗어난 채널
 * 서버가 한 축으로 받는 조건은 화면에서도 한 컨트롤이다. 서로 AND 인 조건은 컨트롤을 나눈다.
 *
 * **개수는 조건부다.** facets 는 목록과 같은 필터를 받는다. 각 축은 **자기 축만 뺀** 나머지
 * 조건으로 센다. 선택지 수는 "다른 조건은 그대로 두고 그 값 하나로 좁혔을 때의 수"다.
 * 그래서 중립 `전체 (N)` 의 N 도 목록 total 이 아니라 **자기 축을 뺀 모집단**이어야 한다:
 *   - enum 축: 서버 enum 전체의 합
 *   - 태그 축: `있음 + 없음`
 *   - 밴드: 칸 합 + 값 없음
 *
 * "전체"는 c6 `neutralValue` 규약에 따라 **빈 문자열 하나**다(rules.md §17). 수는 중립 라벨과
 * `count` 두 곳에만 넣는다. 비중립 옵션의 label 에 넣으면 `episodeFilterChips` 가 그 label 을
 * 칩 문구로 쓰므로 칩 문구에도 수가 들어간다.
 */

import type {
  EpisodeFacetCount,
  EpisodeFacets,
  EpisodeListFilters,
  EpisodeQualityCandidate,
  EpisodeReviewState,
  EpisodeStatus,
} from "@/api";
import {
  ACTION_SOURCE_OPTIONS,
  actionSourceText,
  episodeQualityTagText,
  episodeQualityTagTip,
  episodeFailureLabel,
  episodeTagChoices,
  EPISODE_INVALID_REASON_TEXT,
  EPISODE_VALIDATION_TEXT,
  isCategoryChoiceValue,
  joinTagSelection,
  lockExclusiveTagChoices,
  splitTagSelection,
  type EpisodeTagChoice,
} from "@/components/label";
import { DEVIATION_ALERT_FRACTION, DEVIATION_WARN_FRACTION } from "./diagnostics/bandDeviation";
import {
  applyBandSeverity,
  bandCut,
  bandSeverityCounts,
  BAND_FILTER_AXES,
  BAND_SEVERITY_BANDS,
  hasBandCut,
  readBandSeverity,
  type BandFilterAxis,
  type BandSeverityBand,
} from "./episodeBandFilter";
import {
  applyFreezeRange,
  FREEZE_AXIS_LABEL,
  freezeRangeText,
  readFreezeRange,
} from "./episodeFreezeFilter";
import { durationChipText, hasDurationCut, withoutDuration } from "./episodeDurationFilter";

/** c6 단일 선택의 중립 값. "이 축을 적용하지 않는다" 를 뜻한다. */
export const EPISODE_FILTER_ALL = "";

/**
 * 드롭다운 한 칸. c6 드롭다운 옵션과 형태가 같지만 이 모듈은 c6 에 의존하지 않는다.
 *
 * `count` 는 그 선택지로 좁혔을 때의 에피소드 수다. c6 가 행 오른쪽 배지로 그린다.
 * facets 를 받지 못하면 값이 없다. 그때는 수만 빠지고 축은 그대로 표시한다.
 */
export type EpisodeFilterOption = {
  value: string;
  label: string;
  count?: number;
  description?: string;
  disabled?: boolean;
};

/** 단일 문자열 값을 받는 축의 키. 서버 query 파라미터와 1:1 이다. */
export type EpisodeFilterAxisKey =
  | "search"
  | "review_state"
  | "status"
  | "validation_status"
  | "invalid_reason"
  | "accepted_invalid"
  | "task_failed"
  | "quality_candidate"
  | "band_worst_channel";

// 드롭다운 한 칸으로 그리는 단일 선택 축. 판정 축 여섯과 계산값 축 하나다.
type EpisodeSelectAxisKey =
  | "review_state"
  | "status"
  | "validation_status"
  | "invalid_reason"
  | "accepted_invalid"
  | "task_failed"
  | "quality_candidate";

/** 팝오버에 표시하는 단일 선택 축 하나. */
export type EpisodeFilterAxis = {
  key: EpisodeSelectAxisKey;
  label: string;
  options: readonly EpisodeFilterOption[];
};

/** 다중 선택 태그 축. 결함 축은 서버 파라미터 두 개(`quality_tag`·`quality_category`)를 한 축으로 쓴다. */
export type EpisodeTagAxisKey = "quality" | "action_source";

export type EpisodeTagAxis = {
  key: EpisodeTagAxisKey;
  label: string;
  // 아무것도 고르지 않았을 때의 트리거 문구 `결함: 전체 (N)`. c6 facet 모드의 placeholder 는
  // 라벨 전체를 대체하므로 축 이름도 여기에 넣는다.
  placeholder: string;
  // 배타 잠금을 적용한 선택지.
  options: readonly EpisodeFilterOption[];
  selected: readonly string[];
};

/** 밴드 한 축의 드롭다운. */
export type EpisodeBandAxisControl = {
  axis: BandFilterAxis;
  label: string;
  options: readonly EpisodeFilterOption[];
  value: string;
};

const REVIEW_STATE_TEXT: Record<EpisodeReviewState, string> = {
  unreviewed: "미검토",
  reviewed: "검토 완료",
};

// 표시 상태는 서버 enum 네 개(`pending`·`available`·`orphaned`·`excluded`) 중 **두 개만** 표시한다.
// 나머지 둘은 적재 과정의 중간 상태이므로 리뷰어가 그 값으로 목록을 좁히지 않는다.
const STATUS_OPTIONS: readonly { value: EpisodeStatus; label: string }[] = [
  { value: "available", label: "사용 가능" },
  { value: "excluded", label: "제외됨" },
];

// 서버가 bool 로 받는 축. 드롭다운 값은 `"true"`·`"false"` 문자열이다. 보낼 때 bool 로 바꾼다.
const BOOLEAN_AXES: ReadonlySet<EpisodeFilterAxisKey> = new Set([
  "accepted_invalid",
  "task_failed",
]);

// 결과는 어휘 계약이 아니라 서버 bool 이다. 그래서 `/v1/review-options` 에 없고, 여기서 직접 정한다.
// "아직 안 본 것"은 이 축이 아니라 `검토 상태` 축이 고른다. `기본 성공` 에는 미검토 에피소드도 들어간다.
const FAILURE_OPTIONS: readonly EpisodeFilterOption[] = [
  { value: "true", label: episodeFailureLabel(true) },
  { value: "false", label: episodeFailureLabel(false) },
];

// 품질 후보 상태 문구. 서버 enum 세 값을 모두 표시한다. 세 값의 합이 자기 축을 뺀 모집단이다.
// 검토 완료가 후보를 확인하면 `pending` 에서 `none` 으로 옮긴다. 그래서 두 값은 후보의 유무가
// 아니라 "확인하지 않은 후보" 의 유무다.
const QUALITY_CANDIDATE_TEXT: Record<EpisodeQualityCandidate, string> = {
  pending: "미처리 있음",
  none: "미처리 없음",
  unanalyzed: "미분석",
};

const WARN_PERCENT = Math.round(DEVIATION_WARN_FRACTION * 100);
const ALERT_PERCENT = Math.round(DEVIATION_ALERT_FRACTION * 100);

/**
 * 밴드 구간 문구. 서버는 판정 어휘를 갖지 않으므로 여기 수치가 계약이다.
 *
 * "정상/경고" 대신 "비슷함/크게 다름" 으로 적는다. 밴드는 판정이 아니라 비교다.
 * 밴드를 벗어났다고 잘못된 데이터라는 뜻은 아니다.
 */
const BAND_TEXT: Record<BandSeverityBand, string> = {
  ok: `비슷함 (<${WARN_PERCENT}%)`,
  warn: `자주 다름 (${WARN_PERCENT}–${ALERT_PERCENT}%)`,
  alert: `크게 다름 (≥${ALERT_PERCENT}%)`,
};

// 칩은 폭이 제한되므로 구간 수치를 빼고 이름만 둔다. 수치는 드롭다운이 표시한다.
const BAND_CHIP_TEXT: Record<BandSeverityBand, string> = {
  ok: "비슷함",
  warn: "자주 다름",
  alert: "크게 다름",
};

const BAND_AXIS_TEXT: Record<BandFilterAxis, string> = {
  action: "Action",
  observation: "Observation",
};

export const BAND_AXIS_LABEL = "밴드";
export const DURATION_AXIS_LABEL = "길이";
export const BAND_CHANNEL_AXIS_LABEL = "벗어난 채널";
// 후보 종류가 영상 정지 하나라서 축 이름이 종류를 말한다.
const QUALITY_CANDIDATE_AXIS_LABEL = "영상 정지 후보";

const QUALITY_AXIS_LABEL = "결함";
const ACTION_SOURCE_AXIS_LABEL = "행동 출처";

/**
 * "전체" 칸. 자기 축을 뺀 모집단 수를 받으면 라벨에 inline 으로 넣는다.
 *
 * 트리거는 `${축 이름}: ${선택 옵션의 label}` 로 그려진다. 그래서 아무것도 고르지 않은 상태에서
 * `결과: 전체 (130)` 을 표시하는 방법은 이것뿐이다. 값을 고르면 트리거에서 수가 사라진다.
 * 이것은 의도한 동작이다. 조건을 적용한 뒤의 수는 목록 헤더의 total 이 표시한다.
 */
function neutral(total?: number): EpisodeFilterOption {
  return {
    value: EPISODE_FILTER_ALL,
    label: total === undefined ? "전체" : `전체 (${total})`,
  };
}

/**
 * 옵션에 개수를 추가한다. facets 에서 같은 `value` 를 찾아 `count` 에만 넣는다.
 *
 * **facets 배열을 옵션 목록으로 쓰지 않는다.** 서버는 enum 전체를 보낸다(`status` 네 개,
 * `validation_status` 세 개). `status` 의 선택지는 그보다 적다. facets 를 그대로 쓰면 `pending`·
 * `orphaned` 가 선택지에 추가된다. 선택지는 FE 가 소유하고, 서버는 수만 준다.
 */
function withCounts(
  options: readonly EpisodeFilterOption[],
  counts: readonly EpisodeFacetCount[] | undefined,
): readonly EpisodeFilterOption[] {
  if (counts === undefined) {
    return options;
  }
  return options.map((option) => {
    const found = counts.find((candidate) => candidate.value === option.value);
    return found === undefined ? option : { ...option, count: found.count };
  });
}

// enum 축에서 자기 축을 뺀 모집단. 서버가 수가 0 인 값까지 enum 전체를 보내므로 그 합이다.
function enumTotal(counts: readonly EpisodeFacetCount[] | undefined): number | undefined {
  return counts?.reduce((sum, entry) => sum + entry.count, 0);
}

function reviewAxis(
  key: EpisodeSelectAxisKey,
  label: string,
  options: readonly EpisodeFilterOption[],
  counts: readonly EpisodeFacetCount[] | undefined,
): EpisodeFilterAxis {
  return { key, label, options: withCounts([neutral(enumTotal(counts)), ...options], counts) };
}

/**
 * 팝오버에 표시하는 단일 선택 판정 축 여섯 개. 절차·가시성 축 넷(검토·표시·검증·무효 사유)이 앞에, 결함
 * 수용과 결과가 뒤에 온다.
 * 판정 축은 서로 독립이므로 함께 적용하면 교집합이다. 그래서 "실패가 아닌데 결함이 기록된 것" 처럼
 * 한 축으로는 만들 수 없는 조건을 만들 수 있다.
 */
export function episodeReviewFilterAxes(
  facets: EpisodeFacets | null,
): readonly EpisodeFilterAxis[] {
  return [
    reviewAxis("review_state", "검토 상태", entries(REVIEW_STATE_TEXT), facets?.review_state),
    reviewAxis("status", "표시 상태", STATUS_OPTIONS, facets?.status),
    reviewAxis(
      "validation_status",
      "검증",
      entries(EPISODE_VALIDATION_TEXT),
      facets?.validation_status,
    ),
    // 사유가 없는 에피소드는 어느 선택지에도 들지 않는다. 그래서 선택지 수의 합은 전체 수가 아니다.
    // "전체" 에는 수를 붙이지 않는다.
    {
      key: "invalid_reason",
      label: "무효 사유",
      options: withCounts(
        [neutral(undefined), ...entries(EPISODE_INVALID_REASON_TEXT)],
        facets?.invalid_reason,
      ),
    },
    reviewAxis(
      "accepted_invalid",
      "결함 수용",
      [
        { value: "true", label: "수용함" },
        { value: "false", label: "수용 안 함" },
      ],
      facets?.accepted_invalid,
    ),
    reviewAxis("task_failed", "결과", FAILURE_OPTIONS, facets?.task_failed),
  ];
}

/**
 * 영상 정지 후보 축. 진단이 계산한 값이다. 그래서 판정 축 묶음에 넣지 않고 "계산값" 구분선 아래에
 * 둔다(진단 spec D1).
 *
 * 후보는 제안일 뿐이다. 결함 태그(`결함` 축)와 연결되지 않는다. `미처리 있음` 은 확인하지 않은
 * 후보가 있는 에피소드다. `미처리 없음` 은 분석이 끝났고 후보가 없거나 모두 확인한 에피소드다.
 * `미분석` 은 분석 값이 없는 에피소드다(진단 전, 디코드·분석 실패).
 */
export function episodeQualityCandidateAxis(facets: EpisodeFacets | null): EpisodeFilterAxis {
  return reviewAxis(
    "quality_candidate",
    QUALITY_CANDIDATE_AXIS_LABEL,
    entries(QUALITY_CANDIDATE_TEXT),
    facets?.quality_candidate,
  );
}

/**
 * 다중 선택 태그 축 두 개. 결함(값·갈래·있음/없음)과 행동 출처(값·없음)이다.
 *
 * 결함 선택지와 수는 facets가 준다. 행동 출처는 고정 선택지에 수만 붙인다.
 * 어휘와 갈래 모두 수가 0인 항목까지 온다.
 * 갈래 **소속**은 facets 에 없으므로 `membership`(label-categories)이 준다. 소속을 모르면 null 이다.
 * 그때는 갈래 행을 표시하지만 값은 묶지 않고 나열한다.
 * 적용되어 있지만 선택지에 없는 값(facets 도착 전, 다른 세션이 적용한 값)은 목록 뒤에 추가한다.
 * c6 다중 선택은 옵션에 없는 선택을 다음 토글에서 제거하기 때문이다.
 */
export function episodeTagFilterAxes(
  filters: EpisodeListFilters,
  facets: EpisodeFacets | null,
  membership: ReadonlyMap<string, readonly string[]> | null,
): readonly EpisodeTagAxis[] {
  const qualitySelected = readEpisodeTagAxis(filters, "quality");
  const quality = episodeTagChoices({
    tags: facets?.quality.tags ?? [],
    tagText: episodeQualityTagText,
    tagTip: episodeQualityTagTip,
    any: {
      label: "결함 있음",
      description: "결함 태그가 하나라도 붙은 에피소드입니다.",
      count: facets?.quality.total,
    },
    none: {
      label: "결함 기록 없음",
      description: "결함 태그 기록이 없습니다. 정상 판정을 뜻하지 않습니다.",
      count: facets?.quality.none,
    },
    categories: (facets?.quality.categories ?? []).map((category) => ({
      code: category.code,
      displayName: category.display_name,
      count: category.count,
    })),
    membership,
  });
  const actionSourceSelected = readEpisodeTagAxis(filters, "action_source");
  const actionSource = ACTION_SOURCE_OPTIONS.map((option) => ({
    ...option,
    count:
      option.value === "none"
        ? facets?.action_source.none
        : facets?.action_source.values.find((entry) => entry.value === option.value)?.count,
  }));
  return [
    tagAxis(
      "quality",
      QUALITY_AXIS_LABEL,
      quality,
      qualitySelected,
      facets && facets.quality.total + facets.quality.none,
      episodeQualityTagText,
    ),
    tagAxis(
      "action_source",
      ACTION_SOURCE_AXIS_LABEL,
      actionSource,
      actionSourceSelected,
      facets && facets.action_source.total + facets.action_source.none,
      actionSourceText,
    ),
  ];
}

function tagAxis(
  key: EpisodeTagAxisKey,
  label: string,
  choices: readonly EpisodeTagChoice[],
  selected: readonly string[],
  total: number | null,
  tagText: (tag: string) => string,
): EpisodeTagAxis {
  const known = new Set(choices.map((choice) => choice.value));
  const missing = selected
    .filter((value) => !known.has(value))
    .map((value) => ({
      value,
      label: isCategoryChoiceValue(value) ? categoryCode(value) : tagText(value),
    }));
  return {
    key,
    label,
    placeholder: `${label}: ${neutral(total ?? undefined).label}`,
    options:
      key === "quality"
        ? lockExclusiveTagChoices([...choices, ...missing], selected)
        : [...choices, ...missing],
    selected,
  };
}

function categoryCode(value: string): string {
  return splitTagSelection([value]).categories[0] ?? value;
}

/** 현재 그 태그 축에 적용된 선택. 결함은 값·토큰과 갈래(`category:<code>`)를 한 배열로 합친다. */
export function readEpisodeTagAxis(
  filters: EpisodeListFilters,
  key: EpisodeTagAxisKey,
): readonly string[] {
  if (key === "quality") {
    return joinTagSelection(filters.quality_tag ?? [], filters.quality_category ?? []);
  }
  return filters.action_source ?? [];
}

/**
 * 태그 축 선택을 바꾼 사본. 빈 파라미터는 뺀다. 빈 값으로 보내면 서버가 조건을 적용한다.
 * 결함은 갈래 행을 `quality_category` 로 분리해 보낸다. 서버는 두 파라미터를 한 축의 OR 로 합친다.
 */
export function applyEpisodeTagAxis(
  filters: EpisodeListFilters,
  key: EpisodeTagAxisKey,
  values: readonly string[],
): EpisodeListFilters {
  const rest = { ...filters };
  if (key === "action_source") {
    delete rest.action_source;
    return values.length === 0 ? rest : { ...rest, action_source: values };
  }
  delete rest.quality_tag;
  delete rest.quality_category;
  const { tags, categories } = splitTagSelection(values);
  return {
    ...rest,
    ...(tags.length === 0 ? {} : { quality_tag: tags }),
    ...(categories.length === 0 ? {} : { quality_category: categories }),
  };
}

/**
 * 밴드 두 축의 드롭다운. 두 축은 독립이고 함께 적용하면 AND 이므로 컨트롤도 두 개다.
 * 한 컨트롤에 묶으면 조건부 개수가 결과와 맞지 않는다(`episodeBandFilter.ts` 머리 주석).
 *
 * 구간 수는 facets 의 0.05 칸을 임계값 기준으로 합친 수다. 중립 수는 칸 합 + 값 없음이다
 * (자기 축을 뺀 모집단).
 */
export function episodeBandFilterAxes(
  filters: EpisodeListFilters,
  facets: EpisodeFacets | null,
): readonly EpisodeBandAxisControl[] {
  return BAND_FILTER_AXES.map((axis) => {
    const axisFacet = facets?.band[axis];
    const counts = axisFacet === undefined ? null : bandSeverityCounts(axisFacet);
    const total =
      axisFacet === undefined
        ? undefined
        : axisFacet.bins.reduce((sum, bin) => sum + bin.count, 0) + axisFacet.unmeasured;
    return {
      axis,
      label: `${BAND_AXIS_LABEL} ${BAND_AXIS_TEXT[axis]}`,
      options: [
        neutral(total),
        ...BAND_SEVERITY_BANDS.map((band) => ({
          value: band,
          label: BAND_TEXT[band],
          ...(counts === null ? {} : { count: counts[band] }),
        })),
      ],
      value: readBandSeverity(filters, axis) ?? EPISODE_FILTER_ALL,
    };
  });
}

/**
 * 밴드 두 축 아래의 한 줄. 값이 없어 어느 구간에도 속하지 않는 에피소드 수를 표시한다.
 * "잴 수 없음"은 0% 가 아니다. 그래서 이 줄이 구간 수의 합이 전체보다 작은 이유를 알려 준다.
 * 두 축을 한 줄에 합쳐, 반 칸 드롭다운 아래에서 줄바꿈이 생기지 않게 한다.
 * 해당 에피소드가 없으면 null 이다. 그때는 DOM 도 만들지 않는다.
 */
export function episodeBandUnmeasuredNote(facets: EpisodeFacets | null): string | null {
  if (facets === null) {
    return null;
  }
  const parts = BAND_FILTER_AXES.filter((axis) => facets.band[axis].unmeasured > 0).map(
    (axis) => `${BAND_AXIS_TEXT[axis]} ${facets.band[axis].unmeasured}개`,
  );
  return parts.length === 0
    ? null
    : `진단 값이 없는 에피소드는 구간에 걸리지 않습니다. 해당 에피소드: ${parts.join(" · ")}.`;
}

/** 밴드 한 축의 선택을 반영한다. 중립이면 그 축의 컷을 제거한다. 다른 축은 그대로 둔다. */
export function applyBandAxisValue(
  filters: EpisodeListFilters,
  axis: BandFilterAxis,
  next: string | null,
): EpisodeListFilters {
  const band = next === null || next === EPISODE_FILTER_ALL ? null : (next as BandSeverityBand);
  return applyBandSeverity(filters, axis, band);
}

/**
 * 벗어난 채널 드롭다운. facets 의 채널별 수를 개수 내림차순으로 표시한다.
 * 적용된 채널이 목록에 없으면(조건이 바뀌어 0 이 됐거나 facets 도착 전) 그 채널을 한 칸 추가한다.
 * 그래서 드롭다운이 빈 칸을 그리지 않는다.
 */
export function episodeWorstChannelOptions(
  filters: EpisodeListFilters,
  facets: EpisodeFacets | null,
): readonly EpisodeFilterOption[] {
  const channels: EpisodeFilterOption[] = (facets?.band.worst_channels ?? []).map((entry) => ({
    value: entry.channel,
    label: entry.channel,
    count: entry.count,
  }));
  const current = filters.band_worst_channel;
  if (
    current !== undefined &&
    current !== "" &&
    !channels.some((option) => option.value === current)
  ) {
    channels.push({
      value: current,
      label: current,
      ...(facets === null ? {} : { count: 0 }),
    });
  }
  return [neutral(), ...channels];
}

/** 한 축만 바꾼 사본. 중립 값이면 축을 빼서 서버가 조건을 적용하지 않게 한다. */
export function applyEpisodeFilterAxis(
  filters: EpisodeListFilters,
  key: EpisodeFilterAxisKey,
  next: string | null,
): EpisodeListFilters {
  const rest = { ...filters };
  delete rest[key];
  if (BOOLEAN_AXES.has(key) && (next === "true" || next === "false")) {
    return { ...rest, [key]: next === "true" };
  }
  // 빈 문자열을 그대로 보내면 서버가 422 로 거절한다. 축을 빼는 것이 "전체" 다.
  return next === null || next === EPISODE_FILTER_ALL ? rest : { ...rest, [key]: next };
}

/** 현재 그 축에 적용된 값. 적용되지 않았으면 중립 값이다. 드롭다운이 자기 값을 다시 그릴 때 쓴다. */
export function readEpisodeFilterAxis(
  filters: EpisodeListFilters,
  key: EpisodeFilterAxisKey,
): string {
  const current = filters[key];
  if (typeof current === "boolean") return String(current);
  return typeof current === "string" ? current : EPISODE_FILTER_ALL;
}

/**
 * 행 2 에 표시하는 적용 조건 하나. `next` 는 이 칩을 제거한 뒤의 필터다. 그래서 호출부가
 * 다시 계산하지 않는다.
 *
 * 이름 검색은 칩으로 표시하지 않는다. 입력 칸이 그 값을 이미 보여 주므로 칩은 같은 정보를
 * 반복한다. 대신 `↺ 초기화` 는 검색까지 비운다(§17 "모든 축을 비운다").
 * 다중 선택 축은 축 하나에 칩 하나(`결함: A, B`)이고, ✕ 는 축 전체를 해제한다.
 * 데이터셋 목록 칩과 같은 형태다.
 */
export type EpisodeFilterChip = {
  key: string;
  label: string;
  next: EpisodeListFilters;
};

export function episodeFilterChips(
  filters: EpisodeListFilters,
  facets: EpisodeFacets | null,
): readonly EpisodeFilterChip[] {
  const chips: EpisodeFilterChip[] = [];
  for (const axis of episodeReviewFilterAxes(facets)) {
    pushSelectAxisChip(chips, filters, axis);
  }

  // 칩 문구는 소속을 몰라도 같다(갈래 이름은 facets 가 준다). 소속은 드롭다운 배치에만 쓴다.
  for (const axis of episodeTagFilterAxes(filters, facets, null)) {
    if (axis.selected.length === 0) {
      continue;
    }
    const labels = axis.selected.map(
      (value) => axis.options.find((option) => option.value === value)?.label ?? value,
    );
    chips.push({
      key: axis.key,
      label: `${axis.label}: ${labels.join(", ")}`,
      next: applyEpisodeTagAxis(filters, axis.key, []),
    });
  }

  // 칩 차례는 팝오버 차례를 따른다. 후보 축은 구분선 아래 첫 칸이다.
  pushSelectAxisChip(chips, filters, episodeQualityCandidateAxis(facets));

  if (readFreezeRange(filters)) {
    chips.push({
      key: "freeze_longest",
      label: `${FREEZE_AXIS_LABEL}: ${freezeRangeText(filters.freeze_longest_min ?? 0, filters.freeze_longest_max)}`,
      next: applyFreezeRange(filters, null),
    });
  }

  if (hasDurationCut(filters)) {
    chips.push({
      key: "duration",
      label: `${DURATION_AXIS_LABEL}: ${durationChipText(filters)}`,
      next: withoutDuration(filters),
    });
  }

  for (const axis of BAND_FILTER_AXES) {
    if (!hasBandCut(filters, axis)) {
      continue;
    }
    const band = readBandSeverity(filters, axis);
    chips.push({
      key: `band_${axis}`,
      label: `${BAND_AXIS_LABEL} ${BAND_AXIS_TEXT[axis]}: ${
        band === null ? cutText(bandCut(filters, axis)) : BAND_CHIP_TEXT[band]
      }`,
      next: applyBandSeverity(filters, axis, null),
    });
  }

  const channel = filters.band_worst_channel;
  if (typeof channel === "string" && channel !== "") {
    chips.push({
      key: "band_worst_channel",
      label: `${BAND_CHANNEL_AXIS_LABEL}: ${channel}`,
      next: applyEpisodeFilterAxis(filters, "band_worst_channel", null),
    });
  }

  return chips;
}

// 단일 선택 축에 값이 적용되어 있으면 칩 하나를 더한다.
function pushSelectAxisChip(
  chips: EpisodeFilterChip[],
  filters: EpisodeListFilters,
  axis: EpisodeFilterAxis,
): void {
  const current = readEpisodeFilterAxis(filters, axis.key);
  if (current === EPISODE_FILTER_ALL) {
    return;
  }
  // 후보에 없는 값이 적용되어 있을 수 있다(MCP·다른 세션이 적용한 조건). 그때는 키를 그대로
  // 표시한다. 빈 칩을 표시해 적용된 조건을 숨기지 않는다.
  const option = axis.options.find((candidate) => candidate.value === current);
  chips.push({
    key: axis.key,
    label: `${axis.label}: ${option?.label ?? current}`,
    next: applyEpisodeFilterAxis(filters, axis.key, null),
  });
}

// 구간에 맞지 않는 밴드 컷. 수치 그대로 적는다(없는 끝은 0 · 1).
function cutText(cut: { min: number | undefined; max: number | undefined }): string {
  return `${cut.min ?? 0}–${cut.max ?? 1}`;
}

/**
 * `⚙ 필터` 배지에 표시할 수. **팝오버를 열어야 보이는** 축만 센다.
 *
 * 이름 검색은 레인에 입력 칸으로 보이므로 세지 않는다. 데이터셋 고급 필터의 "레일 밖 조건만
 * 센다" 와 같은 규칙이다. 배지는 보이지 않는 곳에 적용된 조건의 수를 표시한다.
 */
export function episodeSheetFilterCount(filters: EpisodeListFilters): number {
  return episodeFilterChips(filters, null).length;
}

function entries(text: Record<string, string>): EpisodeFilterOption[] {
  return Object.entries(text).map(([value, label]) => ({ value, label }));
}
